/**
 * Utilidades para normalizar y formatear montos en pesos.
 *
 * El historial de pedidos ha devuelto montos con la escala corrida: un valor de
 * $9.637.830 llega como 9637830000000, que es el mismo numero con los 6 decimales
 * de un decimal(18,6) pegados (se pierde el separador decimal en el origen).
 * Estas funciones dejan el front a prueba de esos casos sin romper los valores
 * que ya vienen bien.
 */

// A partir de mil millones un total que ademas es multiplo exacto de un millon
// no corresponde a un pedido real: es la escala corrida x10^6.
const UMBRAL_ESCALA = 1000000000;
const FACTOR_ESCALA = 1000000;

/**
 * Corrige montos que llegan con los decimales pegados (x10^6).
 */
const corregirEscala = (numero) => {
  if (!Number.isFinite(numero)) return 0;

  const absoluto = Math.abs(numero);
  if (
    Number.isInteger(numero) &&
    absoluto >= UMBRAL_ESCALA &&
    numero % FACTOR_ESCALA === 0
  ) {
    return numero / FACTOR_ESCALA;
  }

  return numero;
};

/**
 * Convierte a numero un monto que puede llegar como number o como string en
 * cualquier convencion ("9637830.000000", "9.637.830,50", "$ 1.200.000").
 */
export const parseMonto = (valor) => {
  if (valor === null || valor === undefined || valor === "") return 0;
  if (typeof valor === "number") return corregirEscala(valor);

  // Se deja solo lo que puede formar parte de un numero.
  let texto = String(valor)
    .trim()
    .replace(/[^0-9.,-]/g, "");
  if (texto === "" || texto === "-") return 0;

  const ultimoPunto = texto.lastIndexOf(".");
  const ultimaComa = texto.lastIndexOf(",");

  if (ultimoPunto !== -1 && ultimaComa !== -1) {
    // Vienen los dos separadores: el ultimo es el decimal.
    texto =
      ultimaComa > ultimoPunto
        ? texto.replace(/\./g, "").replace(",", ".")
        : texto.replace(/,/g, "");
  } else if (ultimaComa !== -1) {
    // Solo comas: son miles si quedan grupos exactos de 3 digitos.
    texto = /^-?\d{1,3}(,\d{3})+$/.test(texto)
      ? texto.replace(/,/g, "")
      : texto.replace(",", ".");
  } else if (ultimoPunto !== -1) {
    // Solo puntos: mismo criterio, "1.200.000" son miles y "9637830.000000" no.
    if (/^-?\d{1,3}(\.\d{3})+$/.test(texto)) {
      texto = texto.replace(/\./g, "");
    }
  }

  const numero = Number(texto);
  return Number.isFinite(numero) ? corregirEscala(numero) : 0;
};

/**
 * Devuelve el monto formateado en es-CO sin decimales (sin el signo $).
 */
export const formatMoneda = (valor) =>
  new Intl.NumberFormat("es-CO", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(parseMonto(valor));
